import { Box, Container } from "@material-ui/core";
import React, { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { MeetingLobbyPanel } from "../../components/Meeting/MeetingLobbyPanel";
import { Locale } from "../../core/api/model";
import { useCurrentUser } from "../../core/hooks/user";
import SpinnerModal from "../../core/spinner-modal";
import { useUpdateMeetingMutation } from "../../generated/platform";
import { useMeeting, useViewAs, ViewAs } from '../hooks/meeting';

interface Props {
  locale: Locale;
  showErrorModal: (title: string, description: string, raw?: any) => void;
}

export const MeetingLobby = ({ locale, showErrorModal }: Props) => {
  const { uuid } = useParams<{ uuid: string }>();
  const history = useHistory();
  const intl = useIntl();

  const showError = useCallback(
    (raw?) => {
      showErrorModal(
        intl.formatMessage({
          id: "RequestMeetingError",
          defaultMessage: "Warteraum fehlgeschlagen",
        }),
        intl.formatMessage({
          id: "RequestMeetingInvalidArguments",
          defaultMessage: "Leider konnte der Warteraum nicht angezeigt werden.",
        }),
        raw
      );
    },
    [showErrorModal, intl]
  );
  const { meeting, loading, setMeeting } = useMeeting(uuid, showError);
  const { user, loading: userLoading } = useCurrentUser(showError);
  const viewAs = useViewAs(user, meeting);

  const [
    updateMeetingMutation,
    { data: updateData, loading: updateLoading, error: updateError },
  ] = useUpdateMeetingMutation();

  useEffect(() => {
    if (!updateLoading && updateData) {
      setMeeting(updateData.updateMeeting);
    }

    if (!updateLoading && updateError) {
      showError(updateError);
    }
  }, [updateLoading, updateError, updateData, showError, setMeeting]);

  useEffect(() => {
    if (meeting?.hasEnded) {
      history.push(`/virtual-meet/ended/${meeting.uuid}`)
    }
  }, [meeting, history]);

  useEffect(() => {
    if (viewAs in [ViewAs.Forbidden] && meeting?.corporation?.corpId) {
      history.push(`/corp/${meeting?.corporation.corpId}/products`)
    }
  }, [viewAs, meeting, history])

  return (
    <Box
      component={"main"}
      className="main-background"
      pb={{ xs: 7.5, md: 10 }}
      pt={0.01}
    >
      <Container maxWidth="lg">
        <SpinnerModal active={loading || userLoading} />
        {meeting?.expert &&
          meeting?.corporation &&
          meeting?.product &&
          meeting?.participant && (
            <>
              <MeetingLobbyPanel
                viewAs={viewAs}
                onConfirm={() => {
                  history.push(`/virtual-meet/call/${uuid}`);
                }}
                onCancel={() =>
                  updateMeetingMutation({
                    variables: {
                      id: meeting.id,
                      input: {
                        status: "cancelled",
                      },
                    },
                  })
                    .then((_) =>
                      history.push(
                        `/corp/${meeting.corporation.corpId}/products`
                      )
                    )
                    .catch(showError)
                }
                onCommentChange={(comment) =>
                  updateMeetingMutation({
                    variables: {
                      id: meeting.id,
                      input: {
                        request: comment,
                      },
                    },
                  })
                    .then((d) => {
                      if (d.data?.updateMeeting) {
                        setMeeting(d.data.updateMeeting);
                      }
                    })
                    .catch(showError)
                }
                onDocumentUpload={(file) =>
                  updateMeetingMutation({
                    variables: {
                      id: meeting.id,
                      input: {
                        document: file,
                      },
                    },
                  })
                    .then((d) => {
                      if (d.data?.updateMeeting) {
                        setMeeting(d.data.updateMeeting);
                      }
                    })
                    .catch(showError)
                }
                locale={locale}
                //@ts-ignore
                corporation={meeting.corporation}
                expert={{
                  fullName: `${meeting.expert.partName} ${meeting.expert.partSurname}`,
                  title: `${meeting.expert.partTitle}`,
                  position: `${meeting.expert.partProfPosition}`,
                }}
                participant={{
                  fullName: `${meeting.participant.partName} ${meeting.participant.partSurname}`,
                  title: `${meeting.participant.partTitle}`,
                  position: `${meeting.participant.partProfPosition}`,
                  //@ts-ignore
                  corpName: meeting.participant.corporation[0]?.corpName || {},
                }}
                date={{
                  startDate: meeting.start,
                  endDate: meeting.end,
                }}
                //@ts-ignore
                productName={meeting.product.classificationTitle || {}}
                //@ts-ignore
                productDescription={meeting.product.classificationDesc || {}}
                comment={meeting.request || ""}
                documents={meeting.document ? [meeting.document] : []}
              />
            </>
          )}
      </Container>
    </Box>
  );
};
